import Select from "@mui/material/Select";
import MenuItem from "@mui/material/MenuItem";
import { useSettings } from "../../contexts/SettingsManager/SettingsManager";
import {
  standardTimeZones,
  daylightTimeZones,
  getIsDaylightSavings,
  determineCurrentTimeZone,
} from "../../utils/timeUtils";
import styled, {
  FlattenSimpleInterpolation,
  css,
  createGlobalStyle,
} from "styled-components/macro";
import { lightTextPlaceholder, darkTextPlaceholder } from "constants/colors";
import { useDayStartChanged } from "store/atoms";
import { IconButton, Tooltip } from "@mui/material";
import { Replay } from "@mui/icons-material";
import { useGetPageFromPath } from "utils/customHooks/genericHooks";
import { getIsCypressRunning } from "constants/constants";
import { useAtom } from "jotai";
import { atomWithStorage } from "jotai/utils";
import { CUSTOM_SCROLLBAR_CSS } from "App/CUSTOM_SCROLLBAR_CSS";
import { useIsStorybook } from "utils/customHooks/storyHooks";

/** if user manually selects timezone from the dropdown, don't automatically set it back to the timezone found in the workScheduleConfig (handled in AppHooks > useSetTimeZoneFromWorkscheduleConfig) (resets when workSchedule or customer changes) */
export const isTimezoneManuallyOverriddenAtom = atomWithStorage(
  "isTimezoneManuallyOverriddenAtom",
  false
);

const LOCAL_TIMEZONE = "LOCAL_TIMEZONE";
const EMPTY = "";
export const TimeZoneController = ({
  customCss,
}: {
  customCss?: string | FlattenSimpleInterpolation;
}) => {
  const [, setIsTimezoneManuallyOverridden] = useAtom(
    isTimezoneManuallyOverriddenAtom
  );
  const currentTimezone = determineCurrentTimeZone();
  const EMTPY_TEXT = `-- Local ${putTimeZoneUtcInFront(
    currentTimezone.name
  )} --`;
  const {
    timeZoneLocaleString,
    setTimeZoneLocaleString,
    shouldUseCustomTimeZone,
    setShouldUseCustomTimeZone,
  } = useSettings();
  const [, setDayStartChanged] = useDayStartChanged();
  const { isSettingsPage } = useGetPageFromPath();
  const isStorybook = useIsStorybook();
  const shouldReloadAfterChange =
    !isSettingsPage && !getIsCypressRunning() && !isStorybook;
  return (
    <Styles {...{ $customCss: customCss }}>
      <GlobalAutocompleteStyles />
      <span>Time zone:</span>
      <StyledSelect
        MenuProps={{
          disableScrollLock: true,
        }}
        displayEmpty={true}
        className="dropdown customTimeZoneDropdown"
        value={
          shouldUseCustomTimeZone &&
          timeZoneLocaleString !== currentTimezone.localeString
            ? timeZoneLocaleString
            : EMPTY
        }
        onChange={event => {
          setIsTimezoneManuallyOverridden(true);
          if (
            [EMPTY, currentTimezone.localeString].includes(
              String(event.target.value)
            )
          ) {
            setTimeZoneLocaleString(currentTimezone.localeString);
            setShouldUseCustomTimeZone(false);
          } else {
            setTimeZoneLocaleString(String(event.target.value));
            setShouldUseCustomTimeZone(true);
          }
          if (event.target.value !== timeZoneLocaleString) {
            setDayStartChanged(true);
            if (shouldReloadAfterChange) {
              window.location.reload();
            }
          }
        }}
      >
        <MenuItem
          key={LOCAL_TIMEZONE}
          id={currentTimezone.id}
          value={EMPTY}
          style={{ fontStyle: "italic" }}
        >
          {EMTPY_TEXT}
        </MenuItem>
        {(getIsDaylightSavings(new Date())
          ? daylightTimeZones
          : standardTimeZones
        ).map(timeZone => {
          const isLocal =
            timeZone.localeString === currentTimezone.localeString;
          const timeZoneNameUtcInFront = putTimeZoneUtcInFront(timeZone.name);
          return (
            <MenuItem
              key={timeZone.id}
              id={timeZone.id}
              value={isLocal ? "" : timeZone.localeString}
              data-testid={`UTC${timeZone.name.split("UTC")[1]}`}
              style={{ position: "relative" }}
            >
              {isLocal ? <StyledSpan>{`(Local)`}</StyledSpan> : null}
              {timeZoneNameUtcInFront}
            </MenuItem>
          );
        })}
      </StyledSelect>
      {timeZoneLocaleString !== currentTimezone.localeString && (
        <Tooltip title="Reset to Local">
          <IconButton
            className="btnResetTimezone"
            onClick={() => {
              setTimeZoneLocaleString(currentTimezone.localeString);
              setIsTimezoneManuallyOverridden(true);
              setShouldUseCustomTimeZone(false);
              if (shouldReloadAfterChange) {
                window.location.reload();
              }
            }}
          >
            <Replay />
          </IconButton>
        </Tooltip>
      )}
    </Styles>
  );
};

const Styles = styled.div<{ $customCss?: string | FlattenSimpleInterpolation }>`
  display: flex;
  align-items: center;
  position: relative;
  .btnResetTimezone {
    position: absolute;
    width: 24px;
    height: 24px;
    .MuiSvgIcon-root {
      width: 16px;
      height: 16px;
    }
    top: -16px;
    right: -18px;
  }
  ${p => p.$customCss ?? ``}
`;

const StyledSelect = styled(Select)`
  .MuiInputBase-input {
    padding-top: 12px;
    padding-bottom: 12px;
    ${props =>
      props.value === EMPTY
        ? css`
            color: ${lightTextPlaceholder};
          `
        : ""}
  }
  .MuiOutlinedInput-notchedOutline {
    border-color: ${darkTextPlaceholder};
  }
  [class*="StyledSpan"] {
    top: -3px;
    left: 14px;
  }
`;
const StyledSpan = styled.span`
  position: absolute;
  top: -6px;
  left: 16px;
  font-size: 10px;
`;
function putTimeZoneUtcInFront(timeZoneName: string) {
  const timeZoneNameUtcOffset = timeZoneName.split(" ").slice(-1)[0];
  const timeZoneNameUtcInFront = `${timeZoneNameUtcOffset} ${timeZoneName
    .split(" ")
    .slice(0, -1)
    .join(" ")}`;
  return timeZoneNameUtcInFront;
}

const GlobalAutocompleteStyles = createGlobalStyle`
  .MuiAutocomplete-listbox{
    overflow-x: hidden;
    ${CUSTOM_SCROLLBAR_CSS}
  }
  .MuiAutocomplete-option{
    padding-left: 8px !important;
  }
`;
